import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useCurrentInvestigation } from '../../../hooks';
import { Translate } from 'react-localize-redux';
import { BillActions, documentStatusToIcon } from '.';
import { documentStatusToColor } from '../../../utils/bill';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { formatCurrency, fullDateFromPostgresString, stringDatePostgresToDate } from '../../../utils/index.jsx';
import SearchBox, { FilterItem } from '../../../components/general/SearchBox';
import { Grid, Paper, Typography, FormControl, Button, InputLabel, Select, MenuItem, Card, Chip, Box } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import { ColourButton } from '../../../components/general/mini_components-ts';
import { DocumentStatus } from './types';
import { useHistory } from 'react-router-dom';
import { InsuranceBill, Organization, PaymentRecord, BillItem } from './types';
import { useInsuranceBills } from '../../../hooks/useInsuranceBills';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import { useSnackBarState } from '../../../hooks';
import Modal from '../../../components/general/modal';
import Loader from '../../../components/Loader';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getBillByUuidService, getInsuranceBillByUuidService } from '../../../services/billing';
import { IPatient } from '../../../constants/types';
import { generateInsuranceBillPDF, getBillIdentifier, enhanceBillItemsWithPatientData, formatPatientName, getPatientUuid, fetchPatientsForBillItems } from './insuranceBillUtils';

type InsuranceBillsTableProps = {
    investigationUuid: string;
    organizations: Organization[];
    languageCode: string;
    currency: string;
    onBillClick?: (billUuid: string) => void;
    // For Storybook/testing - bypass hook if provided
    bills?: InsuranceBill[];
    loading?: boolean;
    error?: string | null;
    // Required: Fetch function - real implementation in app, mock in Storybook
    fetchPatientFn: (uuidPatient: string) => Promise<IPatient | null>;
};

const InsuranceBillsTable: React.FC<InsuranceBillsTableProps> = ({
    investigationUuid,
    organizations,
    languageCode,
    currency,
    onBillClick,
    bills: providedBills,
    loading: providedLoading,
    error: providedError,
    fetchPatientFn
}) => {
    const [organizationFilter, setOrganizationFilter] = useState<number | ''>('');
    // SHER-1101: gate PDF export controls behind the tenant functionality.
    const { isPdfExportEnabled } = useCurrentInvestigation();
    const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatus[]>([]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [currentPageTable, setCurrentPageTable] = useState<number>(0);
    const [organizationNameFilter, setOrganizationNameFilter] = useState<string>('');
    const [showModalPDF, setShowModalPDF] = useState(false);
    const [selectedBillForPDF, setSelectedBillForPDF] = useState<InsuranceBill | null>(null);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [patientsMap, setPatientsMap] = useState<Record<string, IPatient>>({});
    const [paymentRecords, setPaymentRecords] = useState<PaymentRecord[]>([]);
    const [fetchingPatients, setFetchingPatients] = useState(false);

    const history = useHistory();
    const { config: pdfConfig } = usePDFConfig();
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();

    // Build filters for API call
    const apiFilters = useMemo(() => {
        const filters: {
            organizationId?: number;
            periodStart?: Date;
            periodEnd?: Date;
            status?: number;
        } = {};

        if (organizationFilter) {
            filters.organizationId = organizationFilter as number;
        }
        if (startDate) {
            filters.periodStart = new Date(startDate);
        }
        if (endDate) {
            filters.periodEnd = new Date(endDate);
        }
        if (documentStatusFilter.length === 1) {
            filters.status = documentStatusFilter[0];
        }

        return filters;
    }, [organizationFilter, startDate, endDate, documentStatusFilter]);

    // Use provided bills for Storybook/testing, otherwise use hook
    const hookResult = useInsuranceBills({
        investigationUuid,
        filters: apiFilters,
        enabled: !providedBills // Disable hook if bills are provided
    });

    const bills = providedBills || hookResult.bills;
    const loading = providedLoading !== undefined ? providedLoading : hookResult.loading;
    const error = providedError !== undefined ? providedError : hookResult.error;
    const refetch = hookResult.refetch;

    useEffect(() => {
        setCurrentPageTable(0);
    }, [organizationNameFilter, documentStatusFilter, organizationFilter, startDate, endDate]);

    // Fetch patients for selected bill
    useEffect(() => {
        const fetchPatients = async () => {
            if (!selectedBillForPDF?.billItems || selectedBillForPDF.billItems.length === 0) {
                setFetchingPatients(false);
                return;
            }
            
            setFetchingPatients(true);
            const patients = await fetchPatientsForBillItems(selectedBillForPDF?.billItems || [], fetchPatientFn);
            setPatientsMap(patients);
            setFetchingPatients(false);
        };

        fetchPatients();
    }, [selectedBillForPDF?.billItems, fetchPatientFn]);

    // Fetch payment records when bill is selected
    useEffect(() => {
        const fetchPaymentRecords = async () => {
            if (!selectedBillForPDF?.uuid || !investigationUuid) {
                setPaymentRecords([]);
                return;
            }
            
            try {
                const response = await getBillByUuidService(investigationUuid, selectedBillForPDF.uuid);
                const billWithPayments = response.bill as unknown as InsuranceBill & { paymentRecords?: PaymentRecord[] };
                setPaymentRecords(billWithPayments?.paymentRecords || []);
            } catch (e) {
                console.error('[InsuranceBillsTable] Error fetching payment records:', e);
                setPaymentRecords([]);
            }
        };

        fetchPaymentRecords();
    }, [selectedBillForPDF?.uuid, investigationUuid]);

    // Enhance billItems with patient data
    const billItemsWithPatientData = useMemo(() => {
        if (!selectedBillForPDF?.billItems || selectedBillForPDF.billItems.length === 0) {
            return [];
        }

        const enhanced = enhanceBillItemsWithPatientData(selectedBillForPDF.billItems, patientsMap);
        return enhanced;
    }, [selectedBillForPDF?.billItems, patientsMap]);

    const handleDownloadBillPDF = useCallback(async () => {
        if (!selectedBillForPDF || !pdfConfig) {
            return;
        }

        try {
            setGeneratingPDF(true);
            await generateInsuranceBillPDF(
                selectedBillForPDF,
                billItemsWithPatientData,
                paymentRecords,
                pdfConfig,
                currency,
                languageCode
            );
            setShowModalPDF(false);
            setSelectedBillForPDF(null);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setShowSnackbar({
                show: true,
                message: 'Failed to generate PDF',
                severity: 'error'
            });
        } finally {
            setGeneratingPDF(false);
        }
    }, [selectedBillForPDF, pdfConfig, billItemsWithPatientData, currency, languageCode, paymentRecords, setShowSnackbar]);

    const handleCloseModal = useCallback(() => {
        setShowModalPDF(false);
        setSelectedBillForPDF(null);
        setPatientsMap({}); // Clear patients when closing modal
    }, []);

    function changePageCallback(currentPage: number) {
        setCurrentPageTable(currentPage);
    }

    const billsRows = useMemo(() => {
        // Sort bills by createdAt descending (newest first)
        const sortedBills = [...bills].sort((a, b) =>
            stringDatePostgresToDate(b.createdAt).getTime() - stringDatePostgresToDate(a.createdAt).getTime()
        );

        return sortedBills
            .filter((bill) => {
                // Filter by organization name if search is active
                if (organizationNameFilter.length > 0) {
                    const matchesName = bill.organizationName
                        .toLowerCase()
                        .includes(organizationNameFilter.toLowerCase());
                    if (!matchesName) return false;
                }
                return true;
            })
            .map((bill, indexBill) => {
                const remainingBalance = bill.totalGross - bill.totalPaid;
                const claimsCount = bill.billItems?.length || 0;

                return {
                    id: indexBill,
                    uuid: bill.uuid,
                    organization: bill.coveragePercentage 
                    ? `${bill.organizationName} (${bill.coveragePercentage}%)`
                    : bill.organizationName,
                    totalGross: formatCurrency(bill.totalGross, currency, languageCode),
                    totalPaid: formatCurrency(bill.totalPaid, currency, languageCode),
                    remaining: formatCurrency(remainingBalance, currency, languageCode),
                    status: renderStatus(bill.status),
                    statusValue: bill.status,
                    claimsCount: claimsCount,
                    dateCreation: fullDateFromPostgresString(languageCode, bill.createdAt),
                    dateCreationRaw: stringDatePostgresToDate(bill.createdAt)
                };
            });
    }, [bills, currency, languageCode, organizationNameFilter]);

    const billsStats = useMemo(() => {
        const displayedBills = bills.filter((bill) => {
            if (organizationNameFilter.length > 0) {
                return bill.organizationName
                    .toLowerCase()
                    .includes(organizationNameFilter.toLowerCase());
            }
            return true;
        });

        return {
            count: displayedBills.length,
            totalGross: displayedBills.reduce((sum, bill) => sum + bill.totalGross, 0),
            totalPaid: displayedBills.reduce((sum, bill) => sum + bill.totalPaid, 0),
            totalPending: displayedBills.reduce((sum, bill) => sum + (bill.totalGross - bill.totalPaid), 0)
        };
    }, [bills, organizationNameFilter]);

    function callbackOrganizationNameTyped(name: string) {
        setOrganizationNameFilter(name);
    }

    function renderStatus(billStatus: DocumentStatus) {
        return (
            <ColourButton
                icon={documentStatusToIcon(billStatus)}
                rgbcolor={documentStatusToColor(billStatus)}
                label=""
            />
        );
    }

    function renderCore() {
        if (loading) {
            return (
                <Paper style={{ padding: '2rem', textAlign: 'center' }}>
                    <Typography>
                        <Translate id="general.loading.loading" />
                    </Typography>
                </Paper>
            );
        }

        if (error) {
            return (
                <Paper style={{ padding: '2rem', textAlign: 'center' }}>
                    <Typography color="error">{error}</Typography>
                    <Button onClick={refetch} style={{ marginTop: '1rem' }}>
                        <Translate id="general.retry" />
                    </Button>
                </Paper>
            );
        }

        if (billsRows.length === 0) {
            return (
                <Paper style={{ padding: '2rem', textAlign: 'center' }}>
                    <Typography>
                        <Translate id="hospital.billing.insurance_bills.no_bills" />
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: '0.5rem', color: '#666' }}>
                        <Translate id="hospital.billing.insurance_bills.auto_created_note" />
                    </Typography>
                </Paper>
            );
        }

        const headCells = [
            {
                id: 'organization',
                alignment: 'left' as const,
                label: <Translate id="hospital.billing.insurance_bills.organization" />
            },
            {
                id: 'totalGross',
                alignment: 'left' as const,
                label: [<Translate id="hospital.billing.bill.total" />, `(${currency})`]
            },
            {
                id: 'totalPaid',
                alignment: 'left' as const,
                label: <Translate id="hospital.billing.bill.paid" />
            },
            {
                id: 'remaining',
                alignment: 'left' as const,
                label: <Translate id="hospital.billing.bill.remaining" />
            },
            {
                id: 'status',
                alignment: 'left' as const,
                label: <Translate id="hospital.billing.bill.status.label" />
            },
            {
                id: 'claimsCount',
                alignment: 'left' as const,
                label: <Translate id="hospital.billing.insurance_bills.claims" />
            },
            {
                id: 'dateCreation',
                alignment: 'right' as const,
                label: <Translate id="hospital.billing.bill.date" />
            }
        ];

        return (
            <EnhancedTable
                noHeader
                headCells={headCells}
                rows={billsRows}
                noSelectable
                selectRow={(index: number) => {
                    const findBill = billsRows.find((bill) => bill.id === index);
                    if (findBill) {
                        if (onBillClick) {
                            onBillClick(findBill.uuid);
                        } else {
                            // Default navigation - would need route setup
                            history.push(`/billing/insurance/${findBill.uuid}`);
                        }
                    }
                }}
                currentPage={currentPageTable}
                changePageCallback={changePageCallback}
                actions={isPdfExportEnabled() ? [
                    {
                        type: 'pdf',
                        check: (bill: any) => bill.statusValue === DocumentStatus.CLOSED,
                        func: (index: number) => {
                            const findBill = billsRows.find((bill) => bill.id === index);
                            if (findBill) {
                                // Find the full bill object from the bills array
                                const fullBill = bills.find((b) => b.uuid === findBill.uuid);
                                if (fullBill) {
                                    console.log('[InsuranceBillsTable] Selected bill for PDF:', fullBill);
                                    console.log('[InsuranceBillsTable] Bill has billItems?', fullBill.billItems);
                                    console.log('[InsuranceBillsTable] billItems count:', fullBill.billItems?.length);
                                    setSelectedBillForPDF(fullBill);
                                    setShowModalPDF(true);
                                }
                            }
                        }
                    }
                ] : []}
            />
        );
    }

    function applyFilterGeneral(
        filter: number[],
        functionAdd: (value: number[]) => void,
        value: number
    ) {
        if (filter && filter.includes(value)) {
            if (filter.length === 1) {
                functionAdd([]);
            } else {
                functionAdd(filter.filter((s) => s !== value));
            }
        } else if (filter) {
            functionAdd([...filter, value]);
        } else {
            functionAdd([value]);
        }
    }

    function applyDocumentStatus(status: DocumentStatus) {
        applyFilterGeneral(documentStatusFilter, setDocumentStatusFilter, status);
    }

    // Organization and Date filters component - all on the same line
    const renderDateFilters = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Organization Select */}
            {organizations.length > 0 && (
                <FormControl variant="outlined" margin="dense" style={{ minWidth: '200px', maxWidth: '220px' }}>
                    <InputLabel id="organization-filter-label">
                        <Translate id="hospital.billing.insurance_bills.organization" />
                    </InputLabel>
                    <Select
                        labelId="organization-filter-label"
                        value={organizationFilter.toString()}
                        onChange={(e) => setOrganizationFilter(e.target.value ? parseInt(e.target.value) : '')}
                        label={<Translate id="hospital.billing.insurance_bills.organization" />}
                    >
                        <MenuItem value="">
                            <Translate id="hospital.billing.insurance_bills.all_organizations" />
                        </MenuItem>
                        {organizationOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {/* Start Date */}
            <FormControl variant="outlined" margin="dense" style={{ maxWidth: '220px' }}>
                <DesktopDatePicker
                    label={<Translate id="hospital.search_box.start_date" />}
                    value={startDate ? dayjs(startDate) : null}
                    onChange={(newDate) => setStartDate(newDate ? newDate.format('YYYY-MM-DD') : '')}
                    slotProps={{
                        textField: {
                            variant: 'outlined',
                            fullWidth: true
                        }
                    }}
                />
            </FormControl>
            {/* End Date */}
            <FormControl variant="outlined" margin="dense" style={{ maxWidth: '220px' }}>
                <DesktopDatePicker
                    label={<Translate id="hospital.search_box.end_date" />}
                    value={endDate ? dayjs(endDate) : null}
                    onChange={(newDate) => setEndDate(newDate ? newDate.format('YYYY-MM-DD') : '')}
                    slotProps={{
                        textField: {
                            variant: 'outlined',
                            fullWidth: true
                        }
                    }}
                />
            </FormControl>
            {/* Clear Dates Button */}
            {(startDate || endDate) && (
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                        setStartDate('');
                        setEndDate('');
                    }}
                    style={{
                        height: '40px',
                        minWidth: '100px',
                        textTransform: 'none'
                    }}
                >
                    <Translate id="hospital.search_box.clear_dates" />
                </Button>
            )}
        </div>
    );

    // Organization options for filter
    const organizationOptions = useMemo(() => {
        return organizations.map((org) => ({
            value: org.id.toString(),
            label: org.name
        }));
    }, [organizations]);

    const filterItems: FilterItem[] = [
        {
            label: 'draft',
            selected: documentStatusFilter.includes(DocumentStatus.DRAFT),
            value: DocumentStatus.DRAFT,
            icon: documentStatusToIcon(DocumentStatus.DRAFT),
            color: documentStatusToColor(DocumentStatus.DRAFT),
            callBack: () => applyDocumentStatus(DocumentStatus.DRAFT)
        },
        {
            label: 'closed',
            selected: documentStatusFilter.includes(DocumentStatus.CLOSED),
            value: DocumentStatus.CLOSED,
            icon: documentStatusToIcon(DocumentStatus.CLOSED),
            color: documentStatusToColor(DocumentStatus.CLOSED),
            callBack: () => applyDocumentStatus(DocumentStatus.CLOSED)
        }
    ];

    return (
        <>
            {showModalPDF && (
                <Modal
                    key="modal-pdf"
                    maxWidth="xs"
                    open={true}
                    title=""
                    closeModal={handleCloseModal}
                >
                    {!pdfConfig || fetchingPatients ? (
                        <Loader />
                    ) : (
                        <Grid container spacing={2} justifyContent="center" style={{ padding: '16px' }}>
                            <Grid item xs={12} style={{ textAlign: 'center' }}>
                                {isPdfExportEnabled() && (
                                <Button
                                    variant="contained"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={handleDownloadBillPDF}
                                    disabled={generatingPDF}
                                    fullWidth
                                >
                                    <Translate id="hospital.billing.bill.download" />
                                </Button>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </Modal>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <SearchBox
                        hideSearchPatient={true}
                        textField={{
                            label: 'hospital.billing.insurance_bills.search_organization',
                            callBack: callbackOrganizationNameTyped
                        }}
                        filterItems={filterItems}
                        extraFilters={renderDateFilters}
                    />
                </Grid>

                {billsRows.length > 0 && (
                    <Grid item xs={12}>
                        <Card style={{ margin: '0 0 10px 0' }}>
                            <Grid container justifyContent="center" padding={2}>
                                <Grid item xs={6}>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                            <Translate id="hospital.billing.insurance_bills.stats.total_bills" /> :
                                        </Typography>
                                        <Chip
                                            label={billsStats.count.toString()}
                                            color="primary"
                                            style={{ fontWeight: 'bold', backgroundColor: '#3579f8', color: '#fff', marginRight: 8 }}
                                            size="small"
                                        />
                                    </Box>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                            <Translate id="hospital.billing.bill.total_cost" /> :
                                        </Typography>
                                        <Chip
                                            label={formatCurrency(billsStats.totalGross, currency, languageCode)}
                                            color="primary"
                                            style={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: '#fff', marginRight: 8 }}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                            <Translate id="hospital.billing.bill.paid" /> :
                                        </Typography>
                                        <Chip
                                            label={formatCurrency(billsStats.totalPaid, currency, languageCode)}
                                            color="primary"
                                            style={{ fontWeight: 'bold', backgroundColor: '#4CAF50', color: '#fff', marginRight: 8 }}
                                            size="small"
                                        />
                                    </Box>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                            <Translate id="hospital.billing.bill.pending_payment" /> :
                                        </Typography>
                                        <Chip
                                            label={formatCurrency(billsStats.totalPending, currency, languageCode)}
                                            color="primary"
                                            style={{ fontWeight: 'bold', backgroundColor: billsStats.totalPending > 0 ? '#f44336' : '#4CAF50', color: '#fff', marginRight: 8 }}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                )}

                <Grid item xs={12}>
                    {renderCore()}
                </Grid>
            </Grid>
        </>
    );
};

export default InsuranceBillsTable;

